import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';

import {
  EdgeConfigurationListItem,
  EdgeLocationGroup,
} from 'features/cluster/autoscaler/edge-configurations';
import { CUSTOM_EDGE_PROVIDER } from 'features/cluster/autoscaler/edge-locations/constants';
import { useSelectionTemplatesQuery } from 'hooks/queries/autoscaler';
import {
  useGetClusterEdgeLocationsQuery,
  useListEdgeConfigurationsQuery,
} from 'hooks/queries/omni';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type UseEdgeConfigurationsWithDetails = {
  groups: EdgeLocationGroup[];
  isLoading: boolean;
  error?: unknown;
  refetch: () => void;
};

export const useEdgeConfigurationsWithDetailsQuery =
  (): UseEdgeConfigurationsWithDetails => {
    const { cluster } = useCluster();

    const {
      edgeConfigurations: baseConfigurations,
      isLoading: isLoadingConfigurations,
      error: errorConfigurations,
      refetch: refetchConfigurations,
    } = useListEdgeConfigurationsQuery({
      clusterId: cluster.id,
    });

    const {
      selectionTemplates,
      isLoading: isLoadingSelectionTemplates,
      error: errorSelectionTemplates,
      refetch: refetchSelectionTemplates,
    } = useSelectionTemplatesQuery({
      clusterId: cluster.id,
    });

    const { edgeLocations, isLoading: isLoadingEdgeLocations } =
      useGetClusterEdgeLocationsQuery({
        clusterId: cluster.id,
      });

    const enrichConfiguration = (
      config: (typeof baseConfigurations)[number]
    ): EdgeConfigurationListItem => {
      const linkedTemplates =
        selectionTemplates?.filter((selectionTemplate) => {
          return selectionTemplate.template?.edgeLocationConfigs?.some(
            (edgeLocationConfig) => {
              const isLocationLinked =
                edgeLocationConfig.edgeLocationId === config.edgeLocationId;
              if (!isLocationLinked) {
                return false;
              }

              const isDefaultConfigUsed = !edgeLocationConfig.edgeConfigId;
              return isDefaultConfigUsed
                ? config.default
                : edgeLocationConfig.edgeConfigId === config.id;
            }
          );
        }) || [];

      const sortedLinkedTemplates = orderBy(
        linkedTemplates,
        'template.name',
        'asc'
      );

      return {
        ...config,
        linkedTemplates: sortedLinkedTemplates,
      };
    };

    // Edge configurations belonging to a custom edge location cannot be edited
    // or re-provisioned from the console, so they're filtered out entirely
    // (along with the empty group header) instead of being rendered disabled.
    const visibleConfigurations =
      edgeLocations && edgeLocations.length > 0
        ? baseConfigurations?.filter((config) => {
            const provider = edgeLocations.find(
              (location) => location.id === config.edgeLocationId
            )?.provider;
            return provider !== CUSTOM_EDGE_PROVIDER;
          })
        : baseConfigurations;

    // Build the grouped data structure that mirrors the domain model:
    // each entry is an edge location with the configurations belonging to it.
    // Within a group the default configuration is placed first; the order of
    // groups is the first-appearance order from the API response. The display
    // name for the group comes from the `edgeLocationName` piece the backend
    // attaches to every EdgeConfiguration; fall back to the id for legacy
    // responses that don't carry the name.
    const groups: EdgeLocationGroup[] = Object.entries(
      groupBy(visibleConfigurations, 'edgeLocationId')
    ).map(([edgeLocationId, configs]) => ({
      edgeLocationId,
      edgeLocationName: configs[0]?.edgeLocationName ?? edgeLocationId,
      configurations: orderBy(configs, ['default'], ['desc']).map(
        enrichConfiguration
      ),
    }));

    return {
      groups,
      isLoading:
        isLoadingConfigurations ||
        isLoadingSelectionTemplates ||
        isLoadingEdgeLocations,
      error: errorConfigurations || errorSelectionTemplates,
      refetch: () => {
        refetchConfigurations();
        refetchSelectionTemplates();
      },
    };
  };
